import React, { useEffect, useState } from "react";
import { Sheet, SheetContent } from "./ui/sheet";
import { useCart } from "../contexts/CartContext";
import { Minus, Plus, Trash2, ArrowRight, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";

export const CartSheet = () => {
    const { open, setOpen, items, setQty, remove, subtotal } = useCart();
    const navigate = useNavigate();
    const [aiComment, setAiComment] = useState("");

    useEffect(() => {
        if (!open || items.length === 0) { setAiComment(""); return; }
        // Detect locale from any item name or default to Greek
        const allNames = items.map(i => i.name).join(" ");
        const locale = /[\u0370-\u03ff]/.test(allNames) ? "el" : "en";
        api.post("/ai/cart-comment", {
            items: items.map(i => ({ name: i.name, qty: i.qty, category: i.category })),
            locale,
        }).then(({ data }) => setAiComment(data.reply)).catch(() => {});
    }, [open, items]);

    return (
        <Sheet open={open} onOpenChange={setOpen}>
            <SheetContent
                data-testid="cart-sheet"
                side="right"
                className="cyber-bg border-l border-[var(--gold)]/30 text-white w-full sm:max-w-md flex flex-col p-0 shadow-[0_0_60px_rgba(244,179,57,0.2)]"
            >
                {/* Header */}
                <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
                    <div className="flex items-center gap-2">
                        <div className="h-2 w-2 rounded-full bg-[var(--gold)] shadow-[0_0_10px_var(--gold)] animate-pulse" />
                        <span className="font-mono text-xs uppercase tracking-[0.3em] text-white/70">Your Order</span>
                    </div>
                    <button onClick={() => setOpen(false)} className="h-8 w-8 grid place-items-center rounded-full hover:bg-white/10">
                        <X size={16} />
                    </button>
                </div>

                {/* AI Comment */}
                {aiComment && items.length > 0 && (
                    <div className="px-5 pt-4">
                        <div className="glass-neon rounded-xl px-4 py-3 flex items-start gap-3">
                            <div className="h-8 w-8 shrink-0 rounded-full bg-gradient-to-br from-[var(--neon-red)] to-[var(--neon-green)] grid place-items-center text-[10px] font-mono font-black text-black">AI</div>
                            <p className="text-sm text-white/90 leading-relaxed">{aiComment}</p>
                        </div>
                    </div>
                )}

                {/* Items */}
                <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
                    {items.length === 0 ? (
                        <div className="text-center py-16" data-testid="empty-cart-msg">
                            <p className="text-white/40 text-sm font-mono">// cart_empty</p>
                            <p className="text-white/60 mt-3 text-sm">Talk to GRILL to start.</p>
                        </div>
                    ) : (
                        items.map((i) => (
                            <div key={i.id} className="flex gap-3 p-3 rounded-xl glass-gold border border-white/10 hover:border-[var(--gold)]/40 transition" data-testid={`cart-item-${i.id}`}>
                                <img src={i.image_url} alt={i.name} className="w-16 h-16 object-cover rounded-lg" />
                                <div className="flex-1 min-w-0">
                                    <div className="font-heading uppercase text-sm truncate">{i.name}</div>
                                    <div className="gold-text text-sm font-bold font-mono">€{i.price.toFixed(2)}</div>
                                    <div className="flex items-center gap-2 mt-2">
                                        <button
                                            data-testid={`qty-decr-${i.id}`}
                                            onClick={() => setQty(i.id, i.qty - 1)}
                                            className="h-7 w-7 grid place-items-center rounded-full border border-white/20 hover:border-[var(--gold)] active:scale-90 transition"
                                        >
                                            <Minus size={12} />
                                        </button>
                                        <span data-testid={`qty-${i.id}`} className="w-6 text-center text-sm font-mono">{i.qty}</span>
                                        <button
                                            data-testid={`qty-incr-${i.id}`}
                                            onClick={() => setQty(i.id, i.qty + 1)}
                                            className="h-7 w-7 grid place-items-center rounded-full border border-white/20 hover:border-[var(--gold)] active:scale-90 transition"
                                        >
                                            <Plus size={12} />
                                        </button>
                                        <button
                                            data-testid={`remove-${i.id}`}
                                            onClick={() => remove(i.id)}
                                            className="ml-auto text-white/40 hover:text-[var(--neon-red)] transition"
                                        >
                                            <Trash2 size={14} />
                                        </button>
                                    </div>
                                </div>
                                <div className="font-bold font-mono text-sm">€{(i.price * i.qty).toFixed(2)}</div>
                            </div>
                        ))
                    )}
                </div>

                {/* Footer */}
                {items.length > 0 && (
                    <div className="border-t border-white/10 p-5 space-y-4">
                        <div className="flex items-center justify-between">
                            <span className="font-mono text-xs uppercase tracking-[0.3em] text-white/60">Total</span>
                            <span data-testid="cart-subtotal" className="font-heading text-2xl font-black gold-text">€{subtotal.toFixed(2)}</span>
                        </div>
                        <button
                            data-testid="checkout-btn"
                            disabled={items.length === 0}
                            onClick={() => { setOpen(false); navigate("/checkout"); }}
                            className="gold-cta w-full h-12 rounded-full font-mono uppercase tracking-widest text-sm font-bold flex items-center justify-center gap-2"
                        >
                            Checkout <ArrowRight size={16} strokeWidth={3} />
                        </button>
                    </div>
                )}
            </SheetContent>
        </Sheet>
    );
};
